import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Table } from './components/Table';
import { Loader } from '../../../../../../../../common/react/components/Loader';
import {
    careerAndSeasonStatsDataSelector,
    careerAndSeasonStatsNavSelector,
    careerAndSeasonStatsDataTypeSelector,
    careerAndSeasonStatsSortSelector} from '../../../../../../store/js/modules/statspro/career-and-season-stats/selectors';
import {
    composeRowData,
    composeColumnData,
} from './utils';
import { FETCH_CAREER_AND_SEASON_STATS, UPDATE_BENCHMARKING, UPDATE_DATA_TYPE, UPDATE_SORT } from '../../../../../../store/js/modules/statspro/career-and-season-stats/actions';
import { getTranslation } from '../../../../../../../../common/react/utils/translations';
import { EmptyState } from '../../../../../../../../common/react/components/EmptyState';

export const CareerAndSeasonStatsTable = () => {
    const dispatch = useDispatch();
    const playerData = useSelector( careerAndSeasonStatsDataSelector );
    const nav = useSelector( careerAndSeasonStatsNavSelector );
    const dataType = useSelector( careerAndSeasonStatsDataTypeSelector );
    const sort = useSelector( careerAndSeasonStatsSortSelector );
    const playerId = new URLSearchParams( location.search ).get( 'playerId' );
    const [ rowData, setRowData ] = useState( [] );
    const seasons = useSelector( state => state.seasons[ `competition_${ nav.competition.id }` ]?.list ?? [] );
    const season = seasons.find( item => item.id === nav.season.id );
    const [ noDataAvailable, setNoDataAvailable] = useState( false );
    
    useEffect( () => {
        // Fetch career stats
        dispatch( FETCH_CAREER_AND_SEASON_STATS.request( { type: 'career', playerId: playerId } ) );
    }, [] );

    useEffect( () => {
        setNoDataAvailable( false );

        if ( dataType.type === 'benchmarkedTotals' || ( nav.season.id !== -1 && dataType.type === 'benchmarkedAverages' ) ) {
            // Reset benchmarking when returning to totals
            dispatch( UPDATE_BENCHMARKING.request( { isActive: false } ) );
            dispatch( UPDATE_DATA_TYPE.request( { type: 'totals' } ) );
            return;
        }
        
        // Career stats
        if ( nav.season.id === -1 && playerData.yearly.length ) {
            // Error handling for when there is no data
            if ( playerData.yearly[ 0 ] === 'no data available' ) {
                setNoDataAvailable( true );
                return;
            }

            if ( dataType.type === 'totals') {
                // Set yearly row data
                setRowData( playerData.yearly.map( item => [
                        item.seasonId,
                        {
                            club: item.team ?? '',
                            totals: { ...item.seasonTotals }
                        }
                ] ) );
            } else if ( dataType.type === 'averages' || dataType.type === 'benchmarkedAverages' ) {
                // Set averages row data
                setRowData( playerData.yearly.map( item => [
                        item.seasonId,
                        {
                            club: item.team ?? '',
                            averages: { ...item.benchmarkedSeasonAverages }
                        }
                ] ) );
            }
        } else if ( nav.season.id !== -1 ) {
            // Season stats
            if ( !playerData.season[ season.providerId ] ) {
                // Fetch for rounds data if it doesn't aleady exist
                setRowData( [] );
                dispatch( FETCH_CAREER_AND_SEASON_STATS.request( { type: 'season', playerId: playerId, seasonId: season.providerId } ) );
            } else {
                // Error handling for when there is no data
                if ( playerData.season[ season.providerId ][ 0 ].error ) {
                    setNoDataAvailable( true );
                    return;
                }

                // Set rounds row data
                setRowData( playerData.season[ season.providerId ].map( item => [
                        '',
                        {
                            opponent: item.opponent ?? '',
                            totals: { ...item.stats },
                            round: { roundNumber: item.roundNumber ?? '' },
                            result: {
                                outcome:  item.result?.includes('W') ? 'W' : ( item.result?.includes('L') ? 'L' : 'D' ),
                                score: item.result?.match(/[0-9]+-[0-9]+/)?.[0]
                            }
                        }
                ] ) );
            }
        }
    }, [ playerData.yearly, playerData.season, nav.season.id, dataType.type ] );

    /**
     * Get columns.
     */
    const columns = useMemo( () => (
        composeColumnData( nav.season.id, nav.category )
    ), [ nav.category, rowData ] );

    /**
     * Get rows.
     */
    const data = useMemo( () => (
        rowData.map( item => composeRowData(
            item,
            columns.filter( col => ![
                    'year',
                    'round',
                    'club',
                    'opponent',
                    'result'
                ].includes( col.accessor )
            ),
            nav.season.id,
            dataType.type
        ) ) 
    ), [ columns ] );

    if ( !data.length && !noDataAvailable ) {
        // Loader for waiting for data
        return (
            <div className="career-and-season-stats__loader">
                <Loader />
            </div>
        );
    } else if ( noDataAvailable ) {
        // Error handling for when there is no data
        return (
            <div className="career-and-season-stats__empty-state">
                <EmptyState titleTranslation="label.stats.error.noStatsAvailable" summaryTranslation="label.stats.error.noStatsAvailable.summary"/>
            </div>
        );
    }

    return (
        <Table
            columns={ columns }
            data={ data }
            modifier="career-and-season-stats"
            navSeasonId={ nav.season.id }
            cssClass={ nav.season.id === -1 ? 'career' : 'season' }
        />
    );
};

